// feat-297 — a table whose column-header row pins to the page directly beneath
// the sticky ScorecardNavigation, and scrolls horizontally in lockstep with the
// body. Used by ServerScorecard (both branches), ShiftMetricsTable (opt-in, from
// Manager/Store scorecards) and Leaderboard. Design: feature design/NOTES.md.
//
// WHY A SEPARATE STRIP. A `sticky top-*` <thead> inside an `overflow-x-auto`
// scroller sticks to that scroller, not to the page — and the scroller never
// scrolls vertically, so the header never pins. The header is therefore drawn a
// second time, in its own <table> inside a `sticky` strip that is a SIBLING of
// the body scroller:
//
//   wrapper (the strip's sticky containing block — its bottom is the table's
//   │        bottom, so the header leaves with the table: AC5)
//   ├─ strip   sticky z-30 overflow-hidden, inline `top` = live nav height
//   │   └─ <table aria-hidden> <colgroup widths measured from the body/> {thead} </table>
//   └─ scroller  (caller's classes, e.g. `overflow-x-auto`) — the only x-scrollbar
//       └─ <table style="margin-top:-{thead height}"> {thead} {tbody} </table>
//
// The body table keeps its real <thead> and lays out exactly as before; the
// negative top margin slides that thead up under the scroller's top edge, where
// the scroller's overflow clips it. It is clipped, not hidden, so it stays in
// the accessibility tree.
//
// ACCESSIBILITY — one set of headers. Screen readers get the body table's own
// <thead>, which keeps the real <th>/<td> column association. The pinned strip's
// copy is `aria-hidden` (it has no interactive content — none of these headers
// sort). The visible copy keeps the same `title` attributes, so hover tooltips
// still work on what the pointer actually hovers.
//
// The page scrolls as one page: there is no vertical scroll box (AC7). The
// strip is `overflow-hidden` on both axes and has no scrollbar; the scroller's
// clipped-above-the-top thead is negative overflow, which is not scrollable.
//
// CARDS. Any `overflow-hidden` ancestor becomes the strip's scroll container and
// the header would never pin to the page. Callers' cards use `overflow-clip`
// instead (clips to the rounded radius, not a scroll container). Do not give
// the scroller a z-index: the strip (z-30) must out-rank everything in it.
import React, { useRef } from 'react';
import {
  SCORECARD_NAV_SELECTOR,
  useHeaderGeometry,
  useNavHeight,
  usePinnedState,
  useSyncedScrollLeft,
} from './hooks';

export interface PinnedHeaderTableProps {
  /** The table's <thead> element. Rendered twice: visible in the pinned strip, clipped (a11y copy) in the body. */
  thead: React.ReactElement;
  /** Everything else in the table — normally the <tbody>. */
  children: React.ReactNode;
  /** Classes for both <table> elements (the body table's className is exactly this). */
  tableClassName?: string;
  /** Classes for the horizontal scroller div (exactly this; nothing is added). */
  scrollerClassName?: string;
  /**
   * Extra classes on the strip, per design NOTES (e.g. `rounded-t-[20px]
   * data-[pinned=true]:rounded-t-none`, or Leaderboard's `border-b border-gray-200`).
   * Literal strings only — Tailwind purges interpolated names.
   */
  stripClassName?: string;
  /**
   * When false, renders the legacy single-table markup (scroller > table >
   * thead + body) with no strip, no measuring and no listeners. Used by
   * ShiftMetricsTable so its out-of-scope consumers are byte-identical.
   */
  enabled?: boolean;
  /** Overridable for tests; defaults to the ScorecardNavigation root. */
  navSelector?: string;
}

const STRIP_BASE =
  'sticky z-30 overflow-hidden data-[pinned=true]:shadow-sm transition-shadow duration-150 motion-reduce:transition-none';

const PinnedHeaderTable: React.FC<PinnedHeaderTableProps> = ({
  thead,
  children,
  tableClassName,
  scrollerClassName,
  stripClassName,
  enabled = true,
  navSelector = SCORECARD_NAV_SELECTOR,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const bodyTableRef = useRef<HTMLTableElement>(null);

  const navHeight = useNavHeight(navSelector, enabled);
  const geometry = useHeaderGeometry(bodyTableRef, enabled);
  const sync = useSyncedScrollLeft(stripRef, scrollerRef, enabled);
  const pinned = usePinnedState(stripRef, wrapperRef, navHeight, enabled);

  // A width change can move the body's max scrollLeft; re-align after layout.
  React.useLayoutEffect(() => {
    if (enabled) sync();
  }, [enabled, sync, geometry]);

  if (!enabled) {
    return (
      <div className={scrollerClassName}>
        <table className={tableClassName}>
          {thead}
          {children}
        </table>
      </div>
    );
  }

  const measured = geometry.tableWidth > 0 && geometry.colWidths.length > 0;

  return (
    <div ref={wrapperRef} data-pinned-header-wrapper="">
      <div
        ref={stripRef}
        data-pinned-header-strip=""
        data-pinned={pinned ? 'true' : 'false'}
        className={stripClassName ? `${STRIP_BASE} ${stripClassName}` : STRIP_BASE}
        style={{ top: navHeight }}
      >
        <table
          aria-hidden="true"
          className={tableClassName}
          style={measured ? { width: geometry.tableWidth, tableLayout: 'fixed' } : undefined}
        >
          {measured && (
            <colgroup>
              {geometry.colWidths.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
          )}
          {thead}
        </table>
      </div>
      <div ref={scrollerRef} className={scrollerClassName}>
        <table
          ref={bodyTableRef}
          className={tableClassName}
          style={geometry.headHeight > 0 ? { marginTop: -geometry.headHeight } : undefined}
        >
          {thead}
          {children}
        </table>
      </div>
    </div>
  );
};

export default PinnedHeaderTable;
